<template>
  <Interior class="flex min-h-screen flex-col big-ellipse overflow-hidden -mb-4 lg:-mb-8">
    <ContainerBasic>
      <HeadingBasic
        :size="1"
        class="text-xl md:!text-3xl lg:!text-5xl text-center md:text-left font-black leading-none tracking-tighter block relative z-[2] py-6 lg:pt-20"
      >
        The ULTIMATE Transfer Portal Resource for College Coaches
      </HeadingBasic>
    </ContainerBasic>

    <!-- Category Navigation -->
    <article class="pl-4 mb-6 hidden md:block mt-5">
      <!-- These are default, showing them here -->
      <CategoryNav size="large" :hide-all-articles="false" allLabel="The Latest" />
    </article>

    <!-- Featured Articles Carousel -->
    <article class="mb-10 w-full max-w-[1620px] mx-auto">
      <Carousel :articles="featuredArticles ?? []" :autoPlay="true" :autoPlayInterval="3000" />
    </article>

    <ContainerBasic>
      <!-- Section Heading: The Latest from 64 -->
      <SectionHeading title="The Latest from 64" :show-arrows="true" />

      <!-- Trending Section (Articles & Players) -->
      <article class="my-10">
        <TrendingSection />
      </article>

      <!-- Section Heading: The Latest from 64 -->
      <SectionHeading title="64 on Socials" :show-arrows="false" />
    </ContainerBasic>

    <article class="mt-16 mb-[8rem]">
      <SocialsSection :posts="socialsPosts" :isLoading="isLoading" />
    </article>
    <div
      class="pt-[6rem] mt-[-12rem] mb-[8rem] bg-[linear-gradient(180deg,_#3A1214_0%,_rgba(57,_17,_20,_0.742682)_25.73%,_rgba(54,_15,_17,_0)_100%)]"
    >
      <ContainerBasic>
        <!-- Section Heading: Testimonials -->
        <SectionHeading title="Testimonials" class="text-white" :show-arrows="true" />

        <article class="my-10">
          <TestimonialsSection />
        </article>
      </ContainerBasic>
    </div>

    <!-- Free Trial Section -->
    <div class="big-ellipse dark:bg-neutral-900">
      <div class="py-14 px-8 lg:px-0 lg:!py-8 relative z-[2]">
        <HeadingBasic :tag-name="'h2'" :size="1" class="text-center mb-4 text-balance"
          >First Month <span class="text-cardinal-600 italic">FREE</span></HeadingBasic
        >
        <HeadingBasic
          :size="3"
          :tag-name="'p'"
          class="text-lg text-center max-w-[544px] mx-auto mb-8"
          >Sign up for our zero-commitment, cancel-any-time
          <span class="text-cardinal-600">Free Trial</span> for full access to 64
          Analytics.</HeadingBasic
        >
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <UButton
            size="xl"
            class="!bg-cardinal !text-white px-6 lg:px-10 justify-center"
            :to="{ path: '/sign-up' }"
            >Start Free Trial</UButton
          >
          <UButton
            size="xl"
            color="white"
            class="px-6 lg:px-10 justify-center"
            :to="{ path: '/sign-up' }"
            >Learn More</UButton
          >
        </div>
      </div>

      <!-- <Features /> -->
      <!-- before:content-[''] before:absolute before:bg-black before:h-[200vh] before:w-[200vw] before:z-[1] before:-translate-x-1/2 before:left-1/2 before:-translate-y-[150px] before:rounded-[100%]  -->
      <HomePageSection>
        <ContainerBasic class="overflow-hidden relative xl:px-0 z-[2]">
          <div class="flex flex-col lg:flex-row gap-8 lg:gap-20">
            <div class="hidden lg:flex flex-1 flex-col justify-center gap-4 py-8 lg:p-8">
              <i class="fal fa-angles-up text-cardinal text-[44px] mb-4"></i>
              <HeadingBasic :size="1" :tag-name="'h2'" class="text-balance !leading-[1.2]">
                Elevate Your Game with Elite Insights
              </HeadingBasic>
              <HeadingBasic :tag-name="'p'" :size="3" class="text-xl text-balance">
                Join Now for Exclusive Access to Advanced Statistics and Our Unique Proprietary
                Ranking System
              </HeadingBasic>
              <!-- divider -->
              <div class="relative z-[2] my-8">
                <div class="bg-cardinal h-2 w-[100vw] absolute left-0"></div>
              </div>
              <HeadingBasic :tag-name="'p'" :size="3" class="text-xl text-balance mb-3">
                This is a must-have resource for:
              </HeadingBasic>
              <ul class="xl:text-3xl list-disc pl-6">
                <li>Prospective players & families</li>
                <li>Current players & families</li>
                <li>College coaches</li>
                <li>College fans</li>
              </ul>
            </div>
            <div class="md:flex-1 2xl:flex-[0_0_600px] relative z-[3]">
              <div class="bg-white dark:bg-neutral-800 p-8 lg:p-20 rounded-xl shadow-2xl">
                <div class="flex flex-col gap-10">
                  <HeadingBasic :size="2"
                    >Unlock Full Access with a 64 Analytics Membership</HeadingBasic
                  >
                  <ul class="flex">
                    <li>
                      <a
                        href="#"
                        @click.prevent="billingType = 'annual'"
                        class="block p-2 border-b-4"
                        :class="{
                          'border-b-gray-400 dark:border-b-gray-200': billingType == 'annual',
                          'border-b-gray-300 dark:border-b-gray-700': billingType !== 'annual',
                        }"
                        >Annual</a
                      >
                    </li>
                    <li>
                      <a
                        href="#"
                        @click.prevent="billingType = 'monthly'"
                        class="block p-2 border-b-4"
                        :class="{
                          'border-b-gray-400 dark:border-b-gray-200': billingType == 'monthly',
                          'border-b-gray-300 dark:border-b-gray-700': billingType !== 'monthly',
                        }"
                        >Monthly</a
                      >
                    </li>
                  </ul>
                  <p>
                    Unlock our complete suite of advanced statistics and proprietary ranking
                    systems.
                  </p>
                  <ul class="list-disc pl-5">
                    <li>Exclusive Access to Proprietary Data and Insights</li>
                    <li>Real-Time NCAA Rankings</li>
                    <li>Advanced Player Performance Analytics</li>
                    <li>No contracts! Cancel any time.</li>
                  </ul>
                  <p class="flex flex-col gap-1">
                    <span class="text-3xl font-bold"
                      >${{ monthlyPriceValue }}<span class="text-base">/mo.</span></span
                    >
                    <span>when you pay {{ billingType == 'annual' ? 'annually' : 'monthly' }}</span>
                  </p>
                  <UButton
                    :to="{ path: '/sign-up' }"
                    size="xl"
                    class="!bg-cardinal-700 !text-white flex items-center justify-center"
                    >Start Free Trial</UButton
                  >
                  <p class="text-center relative inline-block mx-auto text-sm max-w-[120px]">
                    No Commitment! Cancel any time
                    <!-- <img
                      class="left-full absolute -top-[20px]"
                      src="~/assets/images/arrow-up-right.svg"
                      alt=""
                    /> -->
                    <svg
                      class="left-full absolute -top-[20px]"
                      width="56"
                      height="34"
                      viewBox="0 0 56 34"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        class="fill-cardinal dark:fill-white"
                        d="M23.4626 32.766L23.4731 33.766L23.4626 32.766ZM48.1897 6.97412L49.1891 6.94239L49.1891 6.94239L48.1897 6.97412ZM48.6843 0.270813C48.2816 -0.107123 47.6487 -0.0870329 47.2708 0.315686L41.112 6.87837C40.734 7.28109 40.7541 7.91394 41.1569 8.29187C41.5596 8.66981 42.1924 8.64972 42.5704 8.247L48.0449 2.4135L53.8784 7.88801C54.2811 8.26595 54.9139 8.24586 55.2919 7.84314C55.6698 7.44042 55.6497 6.80758 55.247 6.42964L48.6843 0.270813ZM1.01042 33.9999L23.4731 33.766L23.4522 31.7661L0.989584 32.0001L1.01042 33.9999ZM49.1891 6.94239L48.9995 0.96827L47.0005 1.03173L47.1902 7.00585L49.1891 6.94239ZM23.4731 33.766C38.0486 33.6141 49.6517 21.5114 49.1891 6.94239L47.1902 7.00585C47.6171 20.4542 36.9066 31.6259 23.4522 31.7661L23.4731 33.766Z"
                      />
                    </svg>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ContainerBasic>
      </HomePageSection>
      <div class="sixtyfour-cta-bar bg-cardinal py-6 text-white">
        <ContainerBasic class="flex justify-center relative px-6">
          <img
            class="z-[1] object-fill w-[90px] max-w-none absolute top-1/2 -translate-y-[50%] opacity-[0.3] left-0 hidden 2xl:block"
            src="~/assets/images/logomark-black.svg"
            alt=""
          />
          <img
            class="z-[1] object-fill w-[75px] lg:w-[90px] max-w-none absolute top-12 lg:top-1/2 lg:-translate-y-[50%] hidden 2xl:block opacity-[0.3] right-4 lg:right-0"
            src="~/assets/images/logomark-black.svg"
            alt=""
          />
          <div class="flex flex-col lg:flex-row items-center justify-center lg:gap-14">
            <div>
              <HeadingBasic :size="3" class="text-balance text-center lg:max-w-[480px] lg:text-left"
                >Have Questions For Our Experts?</HeadingBasic
              >
              <p class="text-center lg:text-left mb-5 lg:mb-0">
                We have top-notch support ready to answer any questions you may have
              </p>
            </div>
            <ButtonPill
              @click="openIntercom('I need assistance on 64 analytics with the following: ')"
              variant="black"
              icon="fas fa-headset"
              >Live Chat
            </ButtonPill>
          </div>
        </ContainerBasic>
      </div>
    </div>
  </Interior>
</template>

<script lang="ts" setup>
useHead({
  title: '64Analytics - College Statistics, Rankings and Analysis',
  meta: [
    {
      name: 'description',
      content: 'Your new home for college statistics, rankings and analysis.',
    },
  ],
});

// import * as Sentry from '@sentry/vue';
import Interior from '~/components/page/Interior.vue';
import SectionHeading from '~/components/home-page/SectionHeading.vue';
import TrendingSection from '~/components/home-page/TrendingSection.vue';
import TestimonialsSection from '~/components/home-page/TestimonialsSection.vue';
import CategoryNav from '~/components/category-nav/CategoryNav.vue';
import Carousel from '~/components/carousel/Carousel.vue';
import SocialsSection from '~/components/socials-section/SocialsSection.vue';
import HomePageSection from '~/components/home-page/Section.vue';
import ButtonPill from '~/components/button/Pill.vue';
import { ConferencesService } from '~/lib/services/conferences.service';
import { useSocialsData } from '~/composables/useSocialsData';
import { useFeaturedArticles } from '~/composables/useFeaturedArticles';

import type { Database } from '~/types';
const { instagramPosts: socialsPosts, isLoading } = useSocialsData(24);
const { featuredArticles } = useFeaturedArticles();

const supabase = useSupabaseClient<Database>();
const toast = useToast();
const { DEFAULT_ANNUAL_PRICE, DEFAULT_MONTHLY_PRICE, annualPrice, monthlyPrice } =
  await useSubscriptionPrices();
const conferencesService = new ConferencesService(supabase);
const { data: conferences } = await useAsyncData('conference', async () => {
  const { data } = await conferencesService.findAll({ perPage: 150 });
  return data;
});

const openIntercom = (message = '') => {
  window.Intercom('showNewMessage', message);
};

const sortedConferences = computed(() => {
  if (!conferences.value) return [];
  return conferences.value
    .slice() // creates a copy of the array to avoid mutating the original array
    .sort((a, b) => {
      // First, compare by division
      if (a.division < b.division) return -1;
      if (a.division > b.division) return 1;

      // If divisions are the same, compare by name
      return a.name.localeCompare(b.name);
    });
});

const filteredConferences = computed(() => {
  if (!currentDivision.value) return [];
  return sortedConferences.value.filter(
    (conference) => conference.division == currentDivision.value
  );
});

const monthlyPriceValue = computed(() => {
  return billingType.value == 'monthly'
    ? ((monthlyPrice.value?.unit_amount || DEFAULT_MONTHLY_PRICE) / 100).toFixed(2)
    : ((annualPrice.value?.unit_amount || DEFAULT_ANNUAL_PRICE) / 12 / 100).toFixed(2);
});

const items = [
  {
    label: 'Annual',
  },
  {
    label: 'Monthly',
  },
];
type NCAADivision = 'D-I' | 'D-II' | 'D-III';
const billingType: Ref<'annual' | 'monthly'> = ref('annual');
const currentDivision = ref<NCAADivision | null>();
const onShowDivision = (division: NCAADivision | null) => {
  currentDivision.value = division;
};

const signupEmail = ref('');
const signupSubmitted = ref(false);
const onSubmitEmail = () => {
  try {
    const { data, status, pending, error } = useFetch('/api/sendgrid/contacts', {
      method: 'POST',
      body: {
        email: signupEmail.value,
      },
    });
    if (error.value) {
      throw error.value;
    }
    if (data.value?.data[0].statusCode == 202) {
      toast.add({
        title: `We have received your request. Keep an eye on your inbox.`,
        color: 'green',
      });
      signupEmail.value = '';
      signupSubmitted.value = true;
    }
  } catch (error) {
    toast.add({
      title: `There was an issue signing you up. Please try again later.`,
      color: 'red',
    });
    console.error(error);
    // Sentry.captureException(error);
  }
};
const images = [
  'https://pbs.twimg.com/media/GUgZy0oXwAAR-Rw.jpg',
  'https://pbs.twimg.com/media/GUgZFEqXwAAezua.jpg',
  'https://pbs.twimg.com/media/GUgY179WAAABSTA.jpg',
  'https://pbs.twimg.com/media/GUgYd2XXIAAoWEs.jpg',
  'https://pbs.twimg.com/media/GTYgCGLXkAA2-3P.png',
  'https://pbs.twimg.com/media/GTYevbFXwAAWnUl.png',
];

const hlsUrl =
  'https://64-analytics-testimonials.s3.amazonaws.com/videos/will-davis-hls/master.m3u8';
</script>

<style lang="scss">
.sixtyfour-cta-bar {
  .formkit-form {
    @apply lg:flex lg:gap-4 lg:w-auto mt-8 lg:mt-0;
    // hide messages that are not field-specific for layout purposes
    > .formkit-messages {
      @apply hidden;
    }
    > .formkit-outer {
      > .formkit-wrapper {
        > .formkit-inner {
          @apply mb-0 max-w-full;
          input {
            @apply bg-white placeholder:text-neutral-950 text-neutral-950 w-full max-w-full;
          }
        }
      }
    }
    .formkit-messages {
      order: 2;
      .text-red-500 {
        @apply text-white;
      }
    }
    .formkit-actions {
      order: 1;
      .formkit-wrapper {
        @apply mb-0;
        button {
          @apply border w-full lg:w-auto mt-3 lg:mt-0 border-white bg-transparent py-[9px];
        }
      }
    }
    .formkit-outer {
      @apply mb-0;
    }
  }
}

.big-ellipse {
  background-image: url(~/assets/images/big-ellipse-light.png);
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-position: top;
  background-size: cover;
}
.dark {
  .big-ellipse {
    background-image: url(~/assets/images/big-ellipse.png);
  }
}
</style>
